import { useCallback, useEffect, useState, useTransition } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { COUNTRIES } from '@wareclouds/frontend-common';

import { CONTEXT } from '@/config/consts/commons';
import {
  getAnswers,
  getCompletedSections,
  saveAnswer,
} from '@/features/onboarding/training/services';
import { getTrainingVariant } from '../utils/trainingConfig';
import { useGetQuestionnaire } from './useGetQuestionnaire';

export function useTrainingSection(sectionId) {
  const { t } = useTranslation(['onboarding']);
  const navigate = useNavigate();

  const variant = getTrainingVariant();
  const questionnaire = useGetQuestionnaire(variant);

  const section = questionnaire?.sections?.find((s) => s.id === sectionId);

  const isAlreadyCompleted = getCompletedSections().includes(sectionId);
  const [currentStep, setCurrentStep] = useState(() =>
    isAlreadyCompleted ? 1 : 0
  );
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState(() => getAnswers());
  const [isSavePending, startSaveTransition] = useTransition();

  const totalSteps = section ? 1 + section.questions.length : 1;
  const isVideoStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const currentQuestion =
    !isVideoStep && section ? section.questions[currentStep - 1] : null;

  const questionNumber = currentStep;

  useEffect(() => {
    if (currentQuestion) {
      const stored = getAnswers()[currentQuestion.globalId];
      setSelectedOption(stored ?? null);
    } else {
      setSelectedOption(null);
    }
  }, [currentQuestion]);

  const handlePrev = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => Math.min(totalSteps - 1, prev + 1));
  }, [totalSteps]);

  const handleSelectOption = useCallback(
    (option) => {
      if (!currentQuestion) return;
      setSelectedOption(option);
      saveAnswer(currentQuestion.globalId, option);
      setAnswers((prev) => ({ ...prev, [currentQuestion.globalId]: option }));
    },
    [currentQuestion]
  );

  const allQuestionsAnswered = section
    ? section.questions.every((q) => answers[q.globalId] != null)
    : false;

  const handleSaveAndReturn = useCallback(() => {
    startSaveTransition(() => {
      navigate('/onboarding/training');
    });
  }, [navigate]);

  const handleBackToMenu = useCallback(() => {
    navigate('/onboarding/training');
  }, [navigate]);

  const translationPath = CONTEXT === COUNTRIES.BR ? COUNTRIES.BR : variant;
  const sectionTitle = section
    ? t(`training.sections.${translationPath}.${sectionId}`)
    : '';

  return {
    sectionTitle,
    sectionPart: section?.part ?? null,

    currentStep,
    totalSteps,
    isVideoStep,
    isLastStep,
    handlePrev,
    handleNext,

    videoId: section?.videoId ?? null,

    currentQuestion,
    questionNumber,
    selectedOption,
    handleSelectOption,

    handleSaveAndReturn,
    handleBackToMenu,
    allQuestionsAnswered,
    isSavePending,
  };
}
